import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { DesktopFrameComponent } from '../../../preview-common/desktop-frame/desktop-frame.component';

/**
 * Skippable in-stream ad on a Google video partner site, desktop. The same
 * placement as {@link VideoPartnersInStreamMobilePreviewComponent}, but the
 * partner page lays the player and its article column side by side, and the ad
 * copy rides on the creative as a companion card instead of sitting above and
 * below it. The 16:9 placement.
 */
@Component({
    selector: 'app-gen-video-partners-in-stream-desktop',
    imports: [CommonModule, DesktopFrameComponent],
    templateUrl: './video-partners-in-stream-desktop.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './video-partners-in-stream-desktop.component.scss'
})
export class VideoPartnersInStreamDesktopPreviewComponent {
  @Input() data!: GenVideoPreviewData;

  /** The creative format this placement previews, found by measured ratio. */
  readonly format: VideoFormat = '16:9';

  /** Skeleton text lines in the article column; the last one is short. */
  readonly railLines = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  /** Thumbnail of the 16:9 video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /** The host, which is all the card shows of the destination. */
  get displayUrl(): string {
    return UrlDisplayHelper.hostOf(this.data.url);
  }

  /**
   * The card gives the headline one line and clips it, so it takes the short
   * headline and only falls back to the long one when there isn't one.
   */
  get headline(): string {
    return this.data.headlines[0] || this.data.longHeadlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
